// The goals bar: a slim bar at the bottom of every screen with today's counts as candles, a panel
// with today and this month side by side where any count opens the list behind it (the tickets,
// the phones, the activations, the bill payments, the accessories with what's selling most), and
// Settings → Goals for the owner's targets.
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { get, onSaved, patch } from '../lib/api';
import { storageGet, storageSet } from '../lib/hooks';
import { useSession } from '../lib/session';
import { Button, Cost, Field, Profit, Spinner, useAction, useToast } from '../ui/kit';
import { Icon } from '../ui/icons';
import { usePrint } from '../ui/print';
import { printPriceStickers, type StickerPhone } from './PriceSticker';
import { EditManySheet } from './Inventory';
import { CANDLE_COLORS, GOAL_KEYS, STATUS_LABELS, TARGETABLE, addDays, formatMoney, goalWords, type CandleStatus, type GoalDetailGroup, type GoalDetailItem, type GoalDetailResponse, type GoalKey, type GoalRow, type GoalsResponse } from '../../shared/goals';
import { usePlan } from '../lib/plan';

const STORE_KEY = 'sos_goals_store';
const REFRESH_MS = 60_000;
/** Saves that change a count: the bar refreshes right after one of these goes through. */
const SAVE_PATHS = /^\/api\/(sales|repairs|buybacks|devices\/[^/]+\/wholesale|repairs\/cancel|goals\/settings)/;

type Range = 'today' | 'month' | 'day';
/** Which count's list is open in the panel: for today, this month, or one calendar day (`date`, "2026-10-21": yesterday, last Tuesday…). */
interface Pick { key: GoalKey; range: Range; date?: string }

// ---------------------------------------------------------------- candles

const colorOf = (s: CandleStatus) => (s === 'amazing' ? CANDLE_COLORS.amazing : s === 'hit' ? CANDLE_COLORS.hit : s === 'under' ? CANDLE_COLORS.under : CANDLE_COLORS.far);

/** A candle: its fill is count / target; full green on target, gold border and star when amazing. */
export function Candle({ status, fill, size = 'sm', label }: { status: CandleStatus; fill: number; size?: 'sm' | 'md' | 'lg'; label?: string }) {
  const w = size === 'lg' ? 34 : size === 'md' ? 22 : 18;
  const h = size === 'lg' ? 64 : size === 'md' ? 42 : 34;
  const pad = size === 'lg' ? 3 : 2;
  const top = size === 'lg' ? 12 : size === 'md' ? 9 : 7;
  const bodyH = h - top - pad;
  const bodyW = w - pad * 2;
  // A little of the colour always shows once there's a count, even far below the target.
  const shown = status === 'none' ? 0 : fill > 0 && fill < 0.08 ? 0.08 : fill;
  const filled = Math.round(bodyH * Math.max(0, Math.min(1, shown)));
  const color = colorOf(status);
  const amazing = status === 'amazing';
  const r = size === 'lg' ? 6 : 4;
  const cx = w / 2;
  const star = (sz: number, y: number) => {
    const pts: string[] = [];
    for (let i = 0; i < 10; i++) {
      const rad = (Math.PI / 5) * i - Math.PI / 2;
      const rr = i % 2 === 0 ? sz : sz * 0.45;
      pts.push(`${(cx + Math.cos(rad) * rr).toFixed(2)},${(y + Math.sin(rad) * rr).toFixed(2)}`);
    }
    return pts.join(' ');
  };
  return (
    <svg className={`goals-candle goals-candle-${status} goals-candle-${size}`} width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label ?? STATUS_LABELS[status]} focusable="false">
      {amazing ? <polygon points={star(size === 'lg' ? 7 : 4, size === 'lg' ? 7 : 4)} fill={CANDLE_COLORS.amazingBorder} /> : <line x1={cx} y1={pad} x2={cx} y2={top} stroke="var(--border-strong)" strokeWidth={size === 'lg' ? 2 : 1.5} strokeLinecap="round" />}
      <rect x={pad} y={top} width={bodyW} height={bodyH} rx={r} fill="var(--cloud)" stroke={amazing ? CANDLE_COLORS.amazingBorder : 'var(--border-strong)'} strokeWidth={amazing ? (size === 'lg' ? 3 : 2) : 1} />
      {filled > 0 ? (
        <rect x={pad + (amazing ? 1 : 0.5)} y={top + bodyH - filled + (amazing ? 1 : 0.5)} width={bodyW - (amazing ? 2 : 1)} height={Math.max(0, filled - (amazing ? 2 : 1))} rx={Math.max(2, r - 1)} fill={color} />
      ) : null}
    </svg>
  );
}

const countText = (g: GoalRow) => (g.target ? `${g.count} / ${g.target}` : String(g.count));

// ---------------------------------------------------------------- the cheer when a goal is met

const CHEER_KEY = 'sos_goals_cheered';
const CHEER_COLORS = ['#0057FF', '#16A34A', '#E0A100', '#FF6B6B', '#8B5CF6', '#00C8E8', '#FF9F1C'];

/**
 * A goal just met (or the amazing level reached) while the bar is on the screen: confetti over the
 * bar and a cheer for a few seconds. Each goal cheers once a day on this device, and only when its
 * count crosses the line while watching (not on the first look of the day, which may be at 8 pm).
 */
function useCheer(data: GoalsResponse | null) {
  const prev = useRef<Record<string, CandleStatus> | null>(null);
  const [cheer, setCheer] = useState<{ id: number; text: string; amazing: boolean } | null>(null);
  useEffect(() => {
    if (!data?.today) return;
    const now: Record<string, CandleStatus> = {};
    for (const g of data.today.goals) now[g.key] = g.status;
    const before = prev.current;
    prev.current = now;
    if (!before) return;
    const up = (st: CandleStatus) => (st === 'amazing' ? 2 : st === 'hit' ? 1 : 0);
    const day = data.today.from;
    const done = storageGet<{ day: number; keys: string[] }>(CHEER_KEY, { day, keys: [] });
    const seen = done.day === day ? done.keys : [];
    const met = data.today.goals.filter((g) => up(g.status) > up(before[g.key] ?? 'none') && !seen.includes(`${g.key}:${g.status}`));
    if (!met.length) return;
    storageSet(CHEER_KEY, { day, keys: [...seen, ...met.map((g) => `${g.key}:${g.status}`)] });
    const amazing = met.some((g) => g.status === 'amazing');
    const names = met.map((g) => `${g.label} ${countText(g)}`).join(' · ');
    setCheer({ id: Date.now(), text: amazing ? `Amazing! ${names}` : `Goal met! ${names}`, amazing });
  }, [data]);
  const clear = useCallback(() => setCheer(null), []);
  return { cheer, clear };
}

/** Confetti bursting up from the bar, and the cheer above it; gone by itself after a few seconds. */
function Cheer({ text, amazing, onDone }: { text: string; amazing: boolean; onDone: () => void }) {
  const pieces = useMemo(() => Array.from({ length: 42 }, (_, i) => ({
    x: 4 + ((i * 37) % 92), dx: ((i * 53) % 160) - 80, r: ((i * 71) % 720) - 360, d: (i * 29) % 400, t: 1500 + ((i * 97) % 900),
    c: CHEER_COLORS[i % CHEER_COLORS.length], w: 6 + (i % 3) * 3, round: i % 4 === 0,
  })), []);
  useEffect(() => {
    const t = setTimeout(onDone, 4200);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div className="goals-cheer" aria-hidden="true">
      {pieces.map((p, i) => {
        const st: Record<string, string | number> = { left: `${p.x}%`, width: p.w, height: p.round ? p.w : p.w * 1.6, background: p.c, animationDelay: `${p.d}ms`, animationDuration: `${p.t}ms`, '--dx': `${p.dx}px`, '--r': `${p.r}deg` };
        return <span key={i} className={`goals-confetti${p.round ? ' round' : ''}`} style={st as CSSProperties} />;
      })}
      <div className={`goals-cheer-banner${amazing ? ' amazing' : ''}`} role="status">
        <Icon.star />
        <span>{text}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- the pop on every count that goes up

const POP_LINES = ['Great job!', 'You’re on the right track!', 'Keep it going!', 'Nice one!', 'That’s how it’s done!', 'Boom!', 'One step closer!', 'Love it!', 'On fire!', 'Let’s go!'];
let popSeq = 0;

/**
 * Every time a count goes up while the bar is on the screen (a repair checked in, a phone sold or
 * bought, an activation, a bill paid, an accessory sold…): the chip bounces, a few confetti pieces
 * fly and a short cheer says how far the goal is. Done on this tablet, it's cheered by name.
 */
function useBumps(data: GoalsResponse | null, mine: () => boolean, name: string) {
  const prev = useRef<{ store: string; day: number; counts: Record<string, number> } | null>(null);
  const [pop, setPop] = useState<{ id: number; text: string; keys: GoalKey[] } | null>(null);
  useEffect(() => {
    if (!data?.today) return;
    const counts: Record<string, number> = {};
    for (const g of data.today.goals) counts[g.key] = g.count;
    const before = prev.current;
    prev.current = { store: data.store, day: data.today.from, counts };
    // The first look, another store's bar, or a new day: nothing to cheer yet.
    if (!before || before.store !== data.store || before.day !== data.today.from) return;
    const up = data.today.goals.filter((g) => g.count > (before.counts[g.key] ?? 0));
    if (!up.length) return;
    const by = mine() && name ? `, ${name}` : '';
    const one = (label: string) => (label.endsWith('ies') ? `${label.slice(0, -3)}y` : label.endsWith('s') ? label.slice(0, -1) : label);
    const parts = up.map((g) => {
      const n = g.count - (before.counts[g.key] ?? 0);
      const left = g.target ? g.target - g.count : null;
      const tail = left === null ? '' : left > 0 ? ` · ${left} to go` : left === 0 ? ' · goal met!' : ` · ${-left} over the goal!`;
      return `+${n} ${n === 1 ? one(g.label) : g.label}${tail}`;
    });
    const line = POP_LINES[popSeq++ % POP_LINES.length];
    setPop({ id: Date.now(), text: `${line.replace('!', `${by}!`)} ${parts.join(' · ')}`, keys: up.map((g) => g.key) });
  }, [data, mine, name]);
  const clear = useCallback(() => setPop(null), []);
  return { pop, clear };
}

/** The small cheer for one more on a count: a short burst and a line above the bar, gone in a few seconds. */
function Pop({ text, onDone }: { text: string; onDone: () => void }) {
  const pieces = useMemo(() => Array.from({ length: 16 }, (_, i) => ({
    x: 30 + ((i * 41) % 40), dx: ((i * 53) % 120) - 60, r: ((i * 71) % 540) - 270, d: (i * 23) % 200, t: 1100 + ((i * 97) % 500),
    c: CHEER_COLORS[i % CHEER_COLORS.length], w: 5 + (i % 3) * 2, round: i % 3 === 0,
  })), []);
  useEffect(() => {
    const t = setTimeout(onDone, 3200);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div className="goals-cheer" aria-hidden="true">
      {pieces.map((p, i) => {
        const st: Record<string, string | number> = { left: `${p.x}%`, width: p.w, height: p.round ? p.w : p.w * 1.6, background: p.c, animationDelay: `${p.d}ms`, animationDuration: `${p.t}ms`, '--dx': `${p.dx}px`, '--r': `${p.r}deg` };
        return <span key={i} className={`goals-confetti${p.round ? ' round' : ''}`} style={st as CSSProperties} />;
      })}
      <div className="goals-pop" role="status"><Icon.spark /><span>{text}</span></div>
    </div>
  );
}

// ---------------------------------------------------------------- the bar

function useGoals(store: string | null) {
  const { me } = useSession();
  const [data, setData] = useState<GoalsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);
  // When this tablet just saved something (a sale, a ticket…): the next load's cheer is for whoever is signed in here.
  const savedAt = useRef(0);
  const load = useCallback(async () => {
    const id = ++seq.current;
    try {
      const r = await get<GoalsResponse>(`/api/goals?range=both${store ? `&store=${encodeURIComponent(store)}` : ''}`);
      if (id === seq.current) { setData(r); setError(null); }
    } catch (e) {
      if (id === seq.current) setError(e instanceof Error ? e.message : 'The goals didn’t load.');
    }
  }, [store]);
  useEffect(() => {
    if (!me) return;
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, REFRESH_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    // Right after a sale, repair check-in, buy, trade-in, activation or wholesale is saved.
    const off = onSaved((path) => { if (SAVE_PATHS.test(path)) { savedAt.current = Date.now(); setTimeout(load, 150); } });
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVisible); off(); };
  }, [me, load]);
  const mine = useCallback(() => Date.now() - savedAt.current < 6000, []);
  return { data, error, reload: load, mine };
}

export function GoalsBar() {
  const { me } = useSession();
  const owner = me?.employee.role_id === 'owner';
  const [store, setStore] = useState<string | null>(() => (owner ? storageGet<string | null>(STORE_KEY, null) : null));
  const { data, reload, mine } = useGoals(owner ? store : null);
  const { cheer, clear: clearCheer } = useCheer(data);
  const { pop, clear: clearPop } = useBumps(data, mine, me?.employee.first_name ?? '');
  const [open, setOpen] = useState(false);
  // The count whose list the panel opens on (a chip on the bar was tapped).
  const [start, setStart] = useState<Pick | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const shown = !!me && !!data?.show_bar && !!data.today;
  // The bar is part of the page: screens with a sticky cart leave room for it (--goals-h).
  useEffect(() => {
    const root = document.documentElement;
    if (!shown || !ref.current) { root.style.setProperty('--goals-h', '0px'); return; }
    const el = ref.current;
    const set = () => root.style.setProperty('--goals-h', `${el.offsetHeight}px`);
    set();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(set) : null;
    ro?.observe(el);
    return () => { ro?.disconnect(); root.style.setProperty('--goals-h', '0px'); };
  }, [shown]);
  if (!shown || !data?.today) return null;
  const pick = (id: string | null) => { setStore(id); storageSet(STORE_KEY, id); };
  return (
    <>
      <div className="goals-bar" ref={ref} role="region" aria-label="Today’s goals">
        <div className="goals-scroll">
          <div className="goals-today">
            <span className="goals-today-k">Today</span>
            <span className="goals-today-d">{data.date.label}</span>
            {data.store !== (me?.location?.id ?? data.store) || data.store === 'all' ? <span className="goals-today-s">{data.store_name}</span> : null}
          </div>
          {data.today.goals.map((g) => (
            <button key={g.key} type="button" className={`goals-chip goals-chip-${g.status}${pop?.keys.includes(g.key) ? ' goals-chip-bump' : ''}`} onClick={() => { setStart({ key: g.key, range: 'today' }); setOpen(true); }} title={g.target ? `${g.label}: ${g.count} of ${g.target} today (${g.status_label.toLowerCase()}) · tap for the list` : `${g.label}: ${g.count} today · tap for the list`}>
              {g.target ? <Candle status={g.status} fill={g.fill} size="lg" label={`${g.label}: ${g.status_label}`} /> : null}
              <span className="goals-chip-text">
                <span className="goals-chip-n">{g.label}</span>
                <span className="goals-chip-v num">{countText(g)}</span>
                {g.target ? <span className="goals-chip-bar" aria-hidden="true"><span style={{ width: `${Math.round(Math.max(0, Math.min(1, g.fill)) * 100)}%` }} /></span> : null}
              </span>
              {g.status === 'amazing' ? <span className="goals-chip-mark" aria-label="Amazing">★</span> : g.status === 'hit' ? <span className="goals-chip-mark" aria-label="Goal met">✓</span> : null}
            </button>
          ))}
          <button type="button" className="goals-open" onClick={() => { setStart(null); setOpen(true); }} aria-haspopup="dialog" aria-expanded={open}>
            <Icon.chart />
            <span>Today and this month</span>
          </button>
        </div>
      </div>
      {cheer ? <Cheer key={cheer.id} text={cheer.text} amazing={cheer.amazing} onDone={clearCheer} /> : pop ? <Pop key={pop.id} text={pop.text} onDone={clearPop} /> : null}
      {open ? <GoalsPanel data={data} owner={!!owner} start={start} onPick={pick} onClose={() => setOpen(false)} onReload={reload} /> : null}
    </>
  );
}

// ---------------------------------------------------------------- the panel

function GoalsPanel({ data, owner, start, onPick, onClose, onReload }: { data: GoalsResponse; owner: boolean; start: Pick | null; onPick: (id: string | null) => void; onClose: () => void; onReload: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [sel, setSel] = useState<Pick | null>(start);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); prev?.focus?.(); };
  }, [onClose]);
  const here = data.store === 'all' ? null : data.store;
  // Tapping the open count again closes its list.
  const toggle = (key: GoalKey, range: Range) => setSel((s) => (s && s.key === key && s.range === range ? null : { key, range }));
  return (
    <div className="goals-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="goals-panel" role="dialog" aria-modal="true" aria-labelledby="goals-panel-title" ref={ref} tabIndex={-1}>
        <div className="goals-panel-head">
          <div>
            <h2 id="goals-panel-title">Goals · {data.store_name}</h2>
            <p className="small muted">{data.date.label} · {data.date.month}, day {data.date.day} of {data.date.days_in_month}</p>
          </div>
          {owner && (data.can_pick_all || data.stores.length > 1) ? (
            <div className="chips goals-stores" role="group" aria-label="Which stores">
              {data.stores.map((s) => (
                <button key={s.id} type="button" className="chip" aria-pressed={here === s.id} onClick={() => onPick(s.id)}>{s.name}</button>
              ))}
              {data.can_pick_all ? <button type="button" className="chip" aria-pressed={data.store === 'all'} onClick={() => onPick('all')}>All stores</button> : null}
            </div>
          ) : null}
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}><Icon.close /></button>
        </div>
        <div className="goals-panel-body">
          {data.today ? <GoalsRow title="Today" sub={data.date.label} goals={data.today.goals} range="today" sel={sel} onPick={toggle} /> : null}
          {sel && sel.range !== 'month' ? <GoalDetail key={`${sel.key}-day`} pick={sel} data={data} onPick={setSel} onClose={() => setSel(null)} /> : null}
          {data.month ? <GoalsRow title="This month" sub={`${data.date.month} 1 – ${data.date.day}`} goals={data.month.goals} range="month" sel={sel} onPick={toggle} /> : null}
          {sel?.range === 'month' ? <GoalDetail key={`${sel.key}-month`} pick={sel} data={data} onPick={setSel} onClose={() => setSel(null)} /> : null}
          <div className="goals-legend" aria-label="What the colours mean">
            <LegendItem status="amazing" text="Amazing: at or above the amazing level" />
            <LegendItem status="hit" text="Target hit" />
            <LegendItem status="under" text="Under target: at least half way" />
            <LegendItem status="far" text="Far from target: below half" />
            <span className="goals-legend-item"><span className="goals-legend-n">12</span><span>No target: just the number</span></span>
          </div>
          <div className="row-wrap goals-panel-foot">
            <span className="small muted">Tap any count for the list behind it, then Yesterday or any day on the calendar for its history. Today is midnight to midnight store time. This month is the 1st to now; its target is the daily target times the days so far.</span>
            {owner ? <a className="btn btn-secondary btn-sm" href="#/settings/goals" onClick={onClose}>Edit targets</a> : null}
            <Button size="sm" variant="ghost" icon="refresh" onClick={onReload}>Refresh</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LegendItem({ status, text }: { status: CandleStatus; text: string }) {
  return (
    <span className="goals-legend-item">
      <Candle status={status} fill={status === 'under' ? 0.6 : status === 'far' ? 0.25 : 1} label={STATUS_LABELS[status]} />
      <span>{text}</span>
    </span>
  );
}

function GoalsRow({ title, sub, goals, range, sel, onPick }: { title: string; sub: string; goals: GoalRow[]; range: Range; sel: Pick | null; onPick: (key: GoalKey, range: Range) => void }) {
  return (
    <section className="goals-section" aria-label={title}>
      <div className="goals-section-head">
        <h3>{title}</h3>
        <span className="small muted">{sub}</span>
      </div>
      <div className="goals-tiles">
        {goals.map((g) => {
          const open = !!sel && sel.key === g.key && sel.range === range;
          return (
            <button key={g.key} type="button" className={`goals-tile goals-tile-${g.status}${open ? ' goals-tile-open' : ''}`} aria-pressed={open} onClick={() => onPick(g.key, range)} title={`${g.label}: see the list`}>
              <span className="goals-tile-top">
                {g.target ? <Candle status={g.status} fill={g.fill} size="lg" label={`${g.label}: ${g.status_label}`} /> : <span className="goals-tile-num num">{g.count}</span>}
                <span className="goals-tile-text">
                  <span className="goals-tile-n">{g.label}</span>
                  <span className="goals-tile-v num">{countText(g)}</span>
                  {g.pill ? <span className={`goals-pill goals-pill-${g.status}`}>{g.pill}</span> : null}
                </span>
              </span>
              <span className="goals-tile-d small muted">{g.money && g.count ? <span className="pill-row"><Cost cents={g.money.cost_cents} /><Profit cents={g.money.profit_cents} /></span> : g.detail}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- the list behind a count

/** "2:41 PM" today, "Oct 3, 2:41 PM" for the month, in store time. */
function whenText(at: number, timeZone: string, range: Range): string {
  const opts: Intl.DateTimeFormatOptions = range === 'month' ? { timeZone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' } : { timeZone, hour: 'numeric', minute: '2-digit' };
  return new Intl.DateTimeFormat('en-US', opts).format(new Date(at));
}

/** A wholesale phone from the list as its sticker prints: the model, the IMEI and its barcode, what it cost and what it sold for. */
function wholesaleSticker(it: GoalDetailItem): StickerPhone | null {
  const cost = it.cost_cents ?? it.sticker_cost_cents;
  if (!it.phone || cost === undefined || it.tag !== 'Wholesale') return null;
  return { ...it.phone, battery_health: null, price_cents: it.amount_cents ?? 0, cost_cents: cost };
}

/** A phone bought or sold, as its price sticker prints (its price in inventory, or what it sold for). */
function priceSticker(it: GoalDetailItem): StickerPhone | null {
  if (!it.phone || !it.device || it.tag === 'Wholesale') return null;
  const price = it.device.price_cents || (it.tag === 'Sold' ? it.amount_cents ?? 0 : 0);
  if (!price) return null;
  return { ...it.phone, battery_health: it.device.battery_health, price_cents: price };
}

/** The sticker a list row prints: the wholesale sticker (cost and sold price) or the price sticker. */
const stickerOf = (it: GoalDetailItem) => wholesaleSticker(it) ?? priceSticker(it);

/** A list on paper: the rows picked (or all of them), with when, what, who and the amount, and the total. */
function GoalListPaper({ title, when, items, timezone, range }: { title: string; when: string; items: GoalDetailItem[]; timezone: string; range: Range }) {
  const total = items.reduce((s, it) => s + (it.amount_cents ?? 0), 0);
  return (
    <div className="dbd-print">
      <h1>{title}</h1>
      <p>{when} · {items.length} item{items.length === 1 ? '' : 's'} · {formatMoney(total)}</p>
      <table className="table dr-table dbd-paper">
        <thead><tr><th>When</th><th>What</th><th>Who</th><th>By</th><th>Status</th><th className="right">Amount</th></tr></thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i}>
              <td className="num">{whenText(it.at, timezone, range)}</td>
              <td>{it.pieces != null && it.pieces !== 1 ? `${it.pieces} × ` : ''}{it.title}{it.sub ? <span className="muted"> · {it.sub}</span> : null}</td>
              <td>{it.who}</td><td>{it.by}</td><td>{it.tag}</td>
              <td className="right num">{it.amount_cents != null ? formatMoney(it.amount_cents) : ''}</td>
            </tr>
          ))}
          <tr className="dr-total"><td colSpan={5}>Total · {items.length}</td><td className="right num">{formatMoney(total)}</td></tr>
        </tbody>
      </table>
    </div>
  );
}

function GoalDetail({ pick, data, onPick, onClose }: { pick: Pick; data: GoalsResponse; onPick: (pick: Pick) => void; onClose: () => void }) {
  const { me } = useSession();
  const print = usePrint();
  const [d, setD] = useState<GoalDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLElement>(null);
  const seq = useRef(0);
  useEffect(() => {
    const id = ++seq.current;
    setError(null);
    get<GoalDetailResponse>(`/api/goals/detail?key=${pick.key}&range=${pick.range === 'month' ? 'month' : 'today'}${pick.range === 'day' && pick.date ? `&date=${pick.date}` : ''}&store=${encodeURIComponent(data.store)}`)
      .then((r) => { if (id === seq.current) setD(r); })
      .catch((e) => { if (id === seq.current) setError(e instanceof Error ? e.message : 'The list didn’t load.'); });
  }, [pick.key, pick.range, pick.date, data]);
  useEffect(() => { ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, [pick.key, pick.range]);
  const label = d?.label ?? data.today?.goals.find((g) => g.key === pick.key)?.label ?? data.month?.goals.find((g) => g.key === pick.key)?.label ?? '';
  // A day from the calendar, the arrows or the history: today is today's live list; any other day is that day's.
  const pickDay = (date: string) => { if (!d || date > d.today_date) return; onPick(date === d.today_date ? { key: pick.key, range: 'today' } : { key: pick.key, range: 'day', date }); };
  // The wholesale list (everyone): a sticker for each phone (cost and sold price), or for all of them at once (50 at a time).
  const stickers = (d?.items ?? []).map(wholesaleSticker).filter((x): x is StickerPhone => !!x);
  const printStickers = (list: StickerPhone[]) => printPriceStickers(print, list.slice(0, 50), me?.settings);
  // Tick rows (or Select all): print their stickers or the list, edit the phones in one go, or put bought phones on sale.
  const [picked, setPicked] = useState<number[]>([]);
  useEffect(() => setPicked([]), [d]);
  const [editing, setEditing] = useState<{ devices: any[]; status?: string } | null>(null);
  const items = d?.items ?? [];
  const chosen = picked.length ? items.filter((_, i) => picked.includes(i)) : [];
  const allPicked = items.length > 0 && picked.length === items.length;
  const togglePick = (i: number) => setPicked((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i]));
  const chosenStickers = chosen.map(stickerOf).filter((x): x is StickerPhone => !!x);
  const chosenDevices = chosen.filter((it) => it.device && it.device.status !== 'sold').map((it) => ({ id: it.device!.id, title: it.device!.title, price_cents: it.device!.price_cents }));
  // Bought phones still being tested or on hold: "Add to inventory" puts them on sale (In stock).
  const toStock = chosen.filter((it) => it.device && ['testing', 'on_hold'].includes(it.device.status)).map((it) => ({ id: it.device!.id, title: it.device!.title, price_cents: it.device!.price_cents }));
  const reload = () => { const id = ++seq.current; get<GoalDetailResponse>(`/api/goals/detail?key=${pick.key}&range=${pick.range === 'month' ? 'month' : 'today'}${pick.range === 'day' && pick.date ? `&date=${pick.date}` : ''}&store=${encodeURIComponent(data.store)}`).then((r) => { if (id === seq.current) setD(r); }).catch(() => undefined); };
  const printList = (list: GoalDetailItem[]) => { if (d) print(<GoalListPaper title={label} when={d.range === 'month' ? `${data.date.month} 1 – ${data.date.day}` : d.range === 'today' ? 'Today' : d.date_label} items={list} timezone={d.timezone} range={pick.range} />, 'letter'); };
  return (
    <section className="goals-detail" aria-label={`${label}: the list`} ref={ref}>
      <div className="goals-detail-head">
        <div className="goals-detail-title">
          <h3>{label}</h3>
          {d ? (
            <span className="small muted">
              {d.count} {d.range === 'month' ? `this month (${data.date.month} 1 – ${data.date.day})` : d.range === 'today' ? 'today' : `on ${d.date_label}`}
              {d.total_cents != null && d.count > 0 ? ` · ${formatMoney(d.total_cents)} ${d.total_label}` : ''}
              {d.store === 'all' ? ' · all stores' : ''}
            </span>
          ) : null}
        </div>
        <div className="chips" role="group" aria-label="Today, yesterday or this month">
          <button type="button" className="chip" aria-pressed={pick.range === 'today'} onClick={() => onPick({ key: pick.key, range: 'today' })}>Today</button>
          <button type="button" className="chip" aria-pressed={pick.range === 'day' && !!d && pick.date === addDays(d.today_date, -1)} disabled={!d} onClick={() => { if (d) onPick({ key: pick.key, range: 'day', date: addDays(d.today_date, -1) }); }}>Yesterday</button>
          <button type="button" className="chip" aria-pressed={pick.range === 'month'} onClick={() => onPick({ key: pick.key, range: 'month' })}>This month</button>
        </div>
        {pick.key === 'wholesale' && stickers.length ? <Button size="sm" icon="tag" onClick={() => printStickers(stickers)} title="A 3 × 2 in sticker for each phone: the model, its IMEI, what it cost and what it sold for">Print {stickers.length === 1 ? 'the sticker' : `${Math.min(stickers.length, 50)} stickers`}</Button> : null}
        <button type="button" className="icon-btn" aria-label="Hide the list" onClick={onClose}><Icon.close /></button>
      </div>
      {d && d.range !== 'month' ? (
        <div className="goals-days" role="group" aria-label="Which day" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, margin: '4px 0 10px' }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => pickDay(addDays(d.date, -1))} title="The day before">‹ Day before</button>
          <input className="input" type="date" value={d.date} max={d.today_date} aria-label="Pick a day" style={{ width: 'auto', minHeight: 36, padding: '4px 8px' }} onChange={(e) => { if (e.target.value) pickDay(e.target.value); }} />
          <button type="button" className="btn btn-secondary btn-sm" disabled={d.date >= d.today_date} onClick={() => pickDay(addDays(d.date, 1))} title="The day after">Day after ›</button>
          <span className="small muted">{d.range === 'today' ? 'Today' : d.date_label}</span>
          <div className="chips" role="group" aria-label="The last two weeks" style={{ flexBasis: '100%' }}>
            {d.days.map((day) => (
              <button key={day.date} type="button" className="chip" aria-pressed={day.date === d.date} title={`${day.label}: ${day.count}`} onClick={() => pickDay(day.date)}>
                {day.label.slice(0, 3)} {Number(day.date.slice(-2))} <span className="num strong">{day.count}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {error ? <p className="small" role="alert">{error}</p> : !d ? <Spinner /> : (
        <>
          {d.groups ? (
            <div className="goals-groups">
              <GroupTable title="Selling most · by product" rows={d.groups.by_product} />
              <GroupTable title="By category" rows={d.groups.by_category} />
            </div>
          ) : null}
          {d.items.length ? (
            <div className="goals-pick-bar" role="region" aria-label="Pick rows">
              <label className="check" style={{ margin: 0 }}>
                <input type="checkbox" className="row-pick" checked={allPicked} onChange={() => setPicked(allPicked ? [] : items.map((_, i) => i))} aria-label="Select all" />
                {picked.length ? `${picked.length} picked` : `Select all ${items.length}`}
              </label>
              <span className="grow" />
              {picked.length ? (
                <>
                  {chosenStickers.length ? <Button size="sm" variant="primary" icon="tag" onClick={() => printStickers(chosenStickers)}>Print {chosenStickers.length} sticker{chosenStickers.length === 1 ? '' : 's'}</Button> : null}
                  <Button size="sm" icon="printer" onClick={() => printList(chosen)}>Print list ({chosen.length})</Button>
                  {chosenDevices.length ? <Button size="sm" icon="edit" onClick={() => setEditing({ devices: chosenDevices })}>Edit {chosenDevices.length}</Button> : null}
                  {toStock.length ? <Button size="sm" icon="boxes" onClick={() => setEditing({ devices: toStock, status: 'in_stock' })} title="Put them on sale: In stock, with a price">Add to inventory ({toStock.length})</Button> : null}
                  <Button size="sm" variant="ghost" onClick={() => setPicked([])}>Clear</Button>
                </>
              ) : (
                <>
                  <Button size="sm" variant="ghost" icon="printer" onClick={() => printList(items)}>Print all</Button>
                  <span className="small muted">Tick rows to print their stickers or the list, edit phones in one go, or add bought phones to inventory</span>
                </>
              )}
            </div>
          ) : null}
          {d.items.length ? (
            <div className="goals-detail-list" role="list">
              {d.items.map((it, i) => (
                <div key={i} className={`goals-detail-row ${picked.includes(i) ? 'goals-detail-picked' : ''}`} role="listitem">
                  <span className="goals-detail-pick"><input type="checkbox" className="row-pick" checked={picked.includes(i)} onChange={() => togglePick(i)} aria-label={`Pick ${it.title}`} /></span>
                  <span className="goals-detail-when num">{whenText(it.at, d.timezone, pick.range)}</span>
                  <span className="goals-detail-main">
                    <span className="goals-detail-what">{it.pieces != null && it.pieces !== 1 ? <span className="goals-detail-pieces num">{it.pieces} ×</span> : null}{it.title}</span>
                    {it.sub ? <span className="small muted">{it.sub}</span> : null}
                    {it.cost_cents !== undefined ? <span className="pill-row"><Cost cents={it.cost_cents} /><Profit cents={it.profit_cents} /></span> : null}
                  </span>
                  <span className="goals-detail-who small">{it.who || <span className="muted">No customer</span>}{it.by ? <span className="muted"> · by {it.by}</span> : null}</span>
                  <span className="goals-detail-tag"><span className="badge">{it.tag}</span></span>
                  <span className="goals-detail-amt num">{it.amount_cents != null ? formatMoney(it.amount_cents) : ''}</span>
                  <span className="goals-detail-ref small muted">{stickerOf(it) ? <Button size="sm" variant="ghost" icon="tag" title={wholesaleSticker(it) ? 'Print its wholesale sticker: the model, IMEI, cost and sold price' : 'Print its price sticker'} aria-label={`Sticker for ${it.title}`} onClick={() => printStickers([stickerOf(it)!])}>Sticker</Button> : it.ref}</span>
                </div>
              ))}
            </div>
          ) : <p className="small muted goals-detail-empty">Nothing {d.range === 'month' ? 'this month yet' : d.range === 'today' ? 'today yet' : `on ${d.date_label}`}.</p>}
          {d.more ? <p className="small muted">The latest {d.limit} are listed; {d.more} more are in the count.</p> : null}
        </>
      )}
      {editing ? <EditManySheet devices={editing.devices} initial={{ status: editing.status }} onClose={() => setEditing(null)} onDone={() => { setEditing(null); setPicked([]); reload(); }} /> : null}
    </section>
  );
}

function GroupTable({ title, rows }: { title: string; rows: GoalDetailGroup[] }) {
  const max = rows.reduce((m, r) => Math.max(m, r.pieces), 0);
  return (
    <div className="goals-group">
      <h4>{title}</h4>
      {rows.length ? rows.map((r) => (
        <div key={r.name} className="goals-group-row">
          <span className="goals-group-name">{r.name}</span>
          <span className="goals-group-bar" aria-hidden="true"><span style={{ width: `${max ? Math.max(4, Math.round((r.pieces / max) * 100)) : 0}%` }} /></span>
          <span className="goals-group-n num">{r.pieces} {r.pieces === 1 ? 'piece' : 'pieces'}</span>
          <span className="goals-group-amt num small muted">{formatMoney(r.sales_cents)}</span>
        </div>
      )) : <p className="small muted">Nothing sold yet.</p>}
    </div>
  );
}

// ---------------------------------------------------------------- Settings → Goals (owner only)

interface SettingsData { show_bar: boolean; targets: Record<GoalKey, { daily: number | null; amazing: number | null }>; hidden: GoalKey[] }

export function GoalsSettingsTab() {
  const { me } = useSession();
  const words = goalWords(usePlan().industry);
  const toast = useToast();
  const { run, busy } = useAction();
  const [s, setS] = useState<SettingsData | null>(null);
  const [showBar, setShowBar] = useState(true);
  const [t, setT] = useState<Record<string, { daily: string; amazing: string }>>({});
  const [hidden, setHidden] = useState<GoalKey[]>([]);
  const load = useCallback(async () => {
    const d = await get<SettingsData>('/api/goals/settings');
    setS(d);
    setShowBar(d.show_bar);
    setHidden(d.hidden);
    setT(Object.fromEntries(GOAL_KEYS.map((k) => [k, { daily: d.targets[k].daily == null ? '' : String(d.targets[k].daily), amazing: d.targets[k].amazing == null ? '' : String(d.targets[k].amazing) }])));
  }, []);
  useEffect(() => { load().catch((e) => toast(e instanceof Error ? e.message : 'The goals didn’t load.', 'error')); }, [load, toast]);
  if (me?.employee.role_id !== 'owner') return <p className="muted">Only the owner can change the goals.</p>;
  if (!s) return <Spinner />;
  const num = (v: string) => (v.trim() === '' ? null : Number(v));
  const save = async () => {
    const targets = Object.fromEntries(TARGETABLE.map((k) => [k, { daily: num(t[k]?.daily ?? ''), amazing: num(t[k]?.amazing ?? '') }]));
    const r = await run(() => patch('/api/goals/settings', { showBar, targets, hidden }), { success: 'Goals saved.' });
    if (r) load();
  };
  const toggleHidden = (k: GoalKey) => setHidden((h) => (h.includes(k) ? h.filter((x) => x !== k) : [...h, k]));
  return (
    <div className="stack">
      <div className="card stack">
        <div className="spread"><h2>Goals bar</h2><span className="badge badge-blue">Owner only</span></div>
        <p className="small muted">A slim bar at the bottom of every screen shows today’s counts as candles: full green when a target is hit (gold when it’s an amazing day), blue when the store is at least half way, red when it’s far off. Tap any count for the list behind it: today, yesterday, any day on the calendar, or this month. Everyone sees their own store; you can switch to all stores. Wholesale cost and profit show for you only.</p>
        <label className="check">
          <input type="checkbox" checked={showBar} onChange={(e) => setShowBar(e.target.checked)} />
          <span className="strong">Show goals bar</span>
        </label>
      </div>
      <div className="card stack">
        <h2>Daily targets</h2>
        <p className="small muted">The month target is the daily target times the days so far this month; the month goal is the daily target times every day in the month. Leave a target empty (or 0) to show just the number.</p>
        <div className="goals-settings">
          {GOAL_KEYS.map((k) => {
            const targetable = TARGETABLE.includes(k);
            return (
              <div key={k} className="goals-setting">
                <div className="goals-setting-head">
                  <span className="strong">{words.labels[k]}</span>
                  <span className="small muted">{words.help[k]}</span>
                </div>
                <div className="goals-setting-fields">
                  {targetable ? (
                    <>
                      <Field label="Target a day"><input className="input" inputMode="numeric" value={t[k]?.daily ?? ''} onChange={(e) => setT({ ...t, [k]: { ...t[k], daily: e.target.value.replace(/\D/g, '') } })} aria-label={`${words.labels[k]} daily target`} placeholder="none" /></Field>
                      <Field label="Amazing day" hint="above the target"><input className="input" inputMode="numeric" value={t[k]?.amazing ?? ''} onChange={(e) => setT({ ...t, [k]: { ...t[k], amazing: e.target.value.replace(/\D/g, '') } })} aria-label={`${words.labels[k]} amazing level`} placeholder="none" /></Field>
                    </>
                  ) : (
                    <span className="small muted goals-setting-none">Shows just the number.</span>
                  )}
                  <label className="check">
                    <input type="checkbox" checked={!hidden.includes(k)} onChange={() => toggleHidden(k)} aria-label={`Show ${words.labels[k]} on the bar`} />
                    <span>Show on the bar</span>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
        <p className="small muted">Counts a shop never uses stay off the bar by themselves (a store that never logged an activation doesn’t see Activations).</p>
        <div className="row-wrap">
          <Button variant="primary" busy={busy} onClick={save}>Save goals</Button>
          <Button variant="ghost" onClick={() => { setT(Object.fromEntries(GOAL_KEYS.map((k) => [k, { daily: k === 'repairs' ? '15' : k === 'phones_bought' ? '12' : k === 'activations' ? '5' : '', amazing: k === 'repairs' ? '20' : k === 'activations' ? '7' : '' }]))); setHidden([]); }}>Back to the starting targets</Button>
        </div>
      </div>
    </div>
  );
}
